import React from 'react';

/**
 * TimeGanttBar - Visual comparison of timesheet vs docket times with OT breakdown
 * Shows a 24-hour timeline with bars representing time ranges
 */
const TimeGanttBar = ({ 
  tsStart, 
  tsEnd, 
  dkStart, 
  dkEnd,
  showLabels = true,
  showTimeLabels = true,
  height = 'h-6',
  ordinaryHours = 8,      // Hours before OT1 kicks in
  ot1Threshold = 10,      // Hours before OT2 kicks in
  breakMinutes = 0,       // Break time to exclude
}) => {
  // Convert time string (HH:MM) to minutes since midnight
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Convert minutes to percentage of 24 hours
  const minutesToPercent = (mins) => {
    if (mins === null) return null;
    return (mins / (24 * 60)) * 100;
  };

  // Format minutes back to time string
  const minutesToTime = (mins) => {
    if (mins === null) return '';
    const h = Math.floor((mins % (24 * 60)) / 60);
    const m = mins % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  // Calculate work duration in hours
  const calculateDuration = (startMins, endMins) => {
    if (startMins === null || endMins === null) return 0;
    let duration = endMins - startMins;
    if (duration < 0) duration += 24 * 60; // Handle overnight
    return (duration - breakMinutes) / 60;
  };

  // Calculate OT segments for a time range
  const calculateOTSegments = (startMins, endMins) => {
    if (startMins === null || endMins === null) return null;
    
    let totalMins = endMins - startMins;
    if (totalMins < 0) totalMins += 24 * 60; // Handle overnight
    
    const workMins = totalMins - breakMinutes;
    const ordinaryMins = Math.min(workMins, ordinaryHours * 60);
    const ot1Mins = Math.min(Math.max(0, workMins - ordinaryHours * 60), (ot1Threshold - ordinaryHours) * 60);
    const ot2Mins = Math.max(0, workMins - ot1Threshold * 60);
    
    // Calculate end times for each segment
    const ordinaryEnd = startMins + ordinaryMins + (breakMinutes > 0 ? breakMinutes / 2 : 0);
    const ot1End = ordinaryEnd + ot1Mins;
    const ot2End = ot1End + ot2Mins;
    
    return {
      ordinary: { start: startMins, end: Math.min(ordinaryEnd, endMins), mins: ordinaryMins },
      ot1: ot1Mins > 0 ? { start: ordinaryEnd, end: Math.min(ot1End, endMins), mins: ot1Mins } : null,
      ot2: ot2Mins > 0 ? { start: ot1End, end: endMins, mins: ot2Mins } : null,
    };
  };

  // Get positions
  const tsStartMins = timeToMinutes(tsStart);
  const tsEndMins = timeToMinutes(tsEnd);
  const dkStartMins = timeToMinutes(dkStart);
  const dkEndMins = timeToMinutes(dkEnd);

  const tsSegments = calculateOTSegments(tsStartMins, tsEndMins);
  const dkSegments = calculateOTSegments(dkStartMins, dkEndMins);

  // Hour markers for the timeline
  const hourMarkers = [0, 6, 12, 18, 24];

  // Render a single bar with OT segments
  const renderBar = (segments, startTime, endTime, isTimesheet = true) => {
    if (!segments) return null;
    
    const baseColor = isTimesheet ? 'amber' : 'blue';
    const yOffset = isTimesheet ? 'top-[15%]' : 'top-[55%]';
    const barHeight = 'h-[30%]';
    
    return (
      <>
        {/* Ordinary hours segment */}
        {segments.ordinary.mins > 0 && (
          <div
            className={`absolute ${yOffset} ${barHeight} rounded-sm border ${
              isTimesheet 
                ? 'bg-green-400/80 border-green-600' 
                : 'bg-green-300/60 border-green-500'
            }`}
            style={{
              left: `${minutesToPercent(segments.ordinary.start)}%`,
              width: `${minutesToPercent(segments.ordinary.end) - minutesToPercent(segments.ordinary.start)}%`,
            }}
            title={`Ordinary: ${minutesToTime(segments.ordinary.start)} → ${minutesToTime(segments.ordinary.end)} (${(segments.ordinary.mins / 60).toFixed(1)}h)`}
          />
        )}
        
        {/* OT1 segment */}
        {segments.ot1 && (
          <div
            className={`absolute ${yOffset} ${barHeight} rounded-sm border ${
              isTimesheet 
                ? 'bg-amber-400/80 border-amber-600' 
                : 'bg-amber-300/60 border-amber-500'
            }`}
            style={{
              left: `${minutesToPercent(segments.ot1.start)}%`,
              width: `${minutesToPercent(segments.ot1.end) - minutesToPercent(segments.ot1.start)}%`,
            }}
            title={`OT1 (1.5x): ${minutesToTime(segments.ot1.start)} → ${minutesToTime(segments.ot1.end)} (${(segments.ot1.mins / 60).toFixed(1)}h)`}
          />
        )}
        
        {/* OT2 segment */}
        {segments.ot2 && (
          <div
            className={`absolute ${yOffset} ${barHeight} rounded-sm border ${
              isTimesheet 
                ? 'bg-red-400/80 border-red-600' 
                : 'bg-red-300/60 border-red-500'
            }`}
            style={{
              left: `${minutesToPercent(segments.ot2.start)}%`,
              width: `${minutesToPercent(segments.ot2.end) - minutesToPercent(segments.ot2.start)}%`,
            }}
            title={`OT2 (2x): ${minutesToTime(segments.ot2.start)} → ${minutesToTime(segments.ot2.end)} (${(segments.ot2.mins / 60).toFixed(1)}h)`}
          />
        )}
        
        {/* Time labels on the bar */}
        {showTimeLabels && startTime && (
          <>
            <span
              className={`absolute text-[8px] font-mono ${isTimesheet ? 'text-amber-800 top-[2px]' : 'text-blue-800 bottom-[2px]'}`}
              style={{ left: `${minutesToPercent(timeToMinutes(startTime))}%`, transform: 'translateX(-50%)' }}
            >
              {startTime}
            </span>
            <span
              className={`absolute text-[8px] font-mono ${isTimesheet ? 'text-amber-800 top-[2px]' : 'text-blue-800 bottom-[2px]'}`}
              style={{ left: `${minutesToPercent(timeToMinutes(endTime))}%`, transform: 'translateX(-50%)' }}
            >
              {endTime}
            </span>
          </>
        )}
      </>
    );
  };

  return (
    <div className="w-full">
      {/* Timeline container */}
      <div className={`relative ${height} bg-slate-100 rounded-sm border border-slate-200 overflow-hidden`}>
        
        {/* Hour grid lines */}
        {hourMarkers.map((hour) => (
          <div
            key={hour}
            className="absolute top-0 bottom-0 border-l border-slate-300/50"
            style={{ left: `${(hour / 24) * 100}%` }}
          />
        ))}

        {/* Row labels */}
        <div className="absolute left-1 top-[15%] text-[7px] text-amber-700 font-medium">TS</div>
        <div className="absolute left-1 top-[55%] text-[7px] text-blue-700 font-medium">DK</div>

        {/* Timesheet bar with OT segments */}
        {renderBar(tsSegments, tsStart, tsEnd, true)}
        
        {/* Docket bar with OT segments */}
        {renderBar(dkSegments, dkStart, dkEnd, false)}
      </div>

      {/* Hour labels */}
      {showLabels && (
        <div className="relative h-3 text-[9px] text-slate-400">
          {hourMarkers.map((hour) => (
            <span
              key={hour}
              className="absolute transform -translate-x-1/2"
              style={{ left: `${(hour / 24) * 100}%` }}
            >
              {hour.toString().padStart(2, '0')}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * TimeGanttLegend - Legend component for the Gantt chart with OT colors
 */
export const TimeGanttLegend = () => (
  <div className="flex items-center gap-3 text-[10px] text-slate-600">
    <div className="flex items-center gap-1">
      <div className="w-3 h-2 bg-green-400 rounded-sm border border-green-600" />
      <span>Ordinary</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-3 h-2 bg-amber-400 rounded-sm border border-amber-600" />
      <span>OT1 (1.5x)</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-3 h-2 bg-red-400 rounded-sm border border-red-600" />
      <span>OT2 (2x)</span>
    </div>
    <span className="text-slate-400 ml-2">|</span>
    <div className="flex items-center gap-1">
      <span className="text-amber-700 font-medium">TS</span>
      <span>=Timesheet</span>
    </div>
    <div className="flex items-center gap-1">
      <span className="text-blue-700 font-medium">DK</span>
      <span>=Docket</span>
    </div>
  </div>
);

export default TimeGanttBar;
